import React, { useState } from 'react'
import { useFirebase } from '../contexts/FirebaseContext'
import toast from 'react-hot-toast'

const SubnoteDataTester = ({ alertId }) => {
  const { testSubnoteDataIntegrity, testFirestoreConnection, addAlertSubnote, getAlertSubnotes } = useFirebase()
  const [testing, setTesting] = useState(false)
  const [subnotes, setSubnotes] = useState([])
  const [loading, setLoading] = useState(false)

  const runFirestoreConnectionTest = async () => {
    setTesting(true)
    try {
      toast.loading('Testing Firestore connection...', { id: 'connection-test' })
      const success = await testFirestoreConnection()
      
      if (success) {
        toast.success('✅ Firestore connection successful!', { id: 'connection-test' })
      } else {
        toast.error('❌ Firestore connection failed!', { id: 'connection-test' })
      }
    } catch (error) {
      toast.error('Error testing connection: ' + error.message, { id: 'connection-test' })
    } finally {
      setTesting(false)
    }
  }

  const runDataIntegrityTest = async () => {
    if (!alertId) {
      toast.error('Alert ID is required for testing')
      return
    }

    setTesting(true)
    try {
      toast.loading('Running data integrity tests...', { id: 'testing' })
      const success = await testSubnoteDataIntegrity(alertId)
      
      if (success) {
        toast.success('✅ All data integrity tests passed!', { id: 'testing' })
      } else {
        toast.error('❌ Some data integrity tests failed', { id: 'testing' })
      }
    } catch (error) {
      toast.error('Error running tests: ' + error.message, { id: 'testing' })
    } finally {
      setTesting(false)
    }
  }

  const addTestSubnote = async () => {
    if (!alertId) {
      toast.error('Alert ID is required')
      return
    }

    try {
      const subnoteData = {
        title: 'Manual Test Subnote',
        message: `Test subnote created at ${new Date().toISOString()}`,
        createdBy: 'test-user@example.com'
      }

      const id = await addAlertSubnote(alertId, subnoteData)
      toast.success(`Subnote created with ID: ${id}`)
      
      // Refresh the list
      await loadSubnotes()
    } catch (error) {
      toast.error('Failed to create subnote: ' + error.message)
    }
  }

  const addFileSubnote = async () => {
    if (!alertId) {
      toast.error('Alert ID is required')
      return
    }

    try {
      const fileSubnoteData = {
        title: 'sample-document.docx',
        message: '<p><strong>Sample Document Content</strong></p><p>This is a test file subnote with HTML content that would normally come from a converted Word document.</p>',
        fileUrl: 'https://example.com/sample.docx',
        fileName: 'sample-document.docx',
        fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        isFileContent: true,
        isWordFile: false,
        createdBy: 'test-user@example.com'
      }

      const id = await addAlertSubnote(alertId, fileSubnoteData)
      toast.success(`File subnote created with ID: ${id}`)
      
      // Refresh the list
      await loadSubnotes()
    } catch (error) {
      toast.error('Failed to create file subnote: ' + error.message)
    }
  }

  const loadSubnotes = async () => {
    if (!alertId) return
    
    setLoading(true)
    try {
      const data = await getAlertSubnotes(alertId)
      setSubnotes(data)
    } catch (error) {
      toast.error('Failed to load subnotes: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
      <h3 className="text-lg font-semibold text-blue-900 mb-4">Subnote Data Storage Tester</h3>
      
      <div className="mb-4">
        <p className="text-sm text-blue-700 mb-2">
          Alert ID: <code className="bg-blue-100 px-2 py-1 rounded">{alertId || 'Not provided'}</code>
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={runFirestoreConnectionTest}
          disabled={testing}
          className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50"
        >
          {testing ? 'Testing...' : 'Test Firestore Connection'}
        </button>
        
        <button
          onClick={runDataIntegrityTest}
          disabled={testing || !alertId}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {testing ? 'Running Tests...' : 'Run Data Integrity Test'}
        </button>
        
        <button
          onClick={addTestSubnote}
          disabled={!alertId}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          Add Test Subnote
        </button>
        
        <button
          onClick={addFileSubnote}
          disabled={!alertId}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
        >
          Add File Subnote
        </button>
        
        <button
          onClick={loadSubnotes}
          disabled={!alertId || loading}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Load Subnotes'}
        </button>
      </div>

      {subnotes.length > 0 && (
        <div className="mt-4">
          <h4 className="text-md font-medium text-gray-900 mb-2">
            Current Subnotes ({subnotes.length})
          </h4>
          <div className="bg-white rounded border max-h-60 overflow-y-auto">
            {subnotes.map((subnote, index) => (
              <div key={subnote.id} className="p-3 border-b last:border-b-0">
                <div className="text-sm">
                  <div className="font-medium text-gray-900">{subnote.title || 'Untitled'}</div>
                  <div className="text-gray-600 truncate">{subnote.message?.substring(0, 100)}...</div>
                  <div className="text-xs text-gray-500 mt-1">
                    ID: {subnote.id} | 
                    {subnote.isFileContent ? ' File Content' : ' Text Content'} |
                    Created: {subnote.createdAt?.toLocaleDateString?.() || 'Unknown'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-blue-600">
        <strong>Expected Path:</strong> <code>/alerts/{alertId}/subnotes/&lt;subnoteId&gt;</code>
      </div>
    </div>
  )
}

export default SubnoteDataTester